<script lang="ts" setup>
import { ref, computed } from "vue";
import {
  getAuth,
  sendEmailVerification,
  createUserWithEmailAndPassword,
  updateProfile,
} from "firebase/auth";
import { app } from "@/fire";
import { useForm, useField } from "vee-validate";
import { useI18n } from "vue-i18n";
import { string, object } from "yup";
import { useRouter } from "vue-router";

const emits = defineEmits(["postLogin", "back"]);

const props = defineProps({
  email: {
    type: String,
    required: true,
  },
});

const router = useRouter();
const auth = getAuth(app);
const { t } = useI18n();

const showPassword = ref(false);

const schema = object({
  email: string()
    .email(t("login.email.error.invalid"))
    .required(t("login.email.error.missing")),

  firstName: string().required(t("login.firstName.error.missing")),

  password: string()
    .required(t("login.password.error.all"))
    .min(8, t("login.password.error.all"))
    .matches(/[a-zA-Z]/, t("login.password.error.all"))
    .matches(/\d/, t("login.password.error.all"))
    .matches(/[@$!%*?&]/, t("login.password.error.all")),
});

const { validate, errors } = useForm({
  validationSchema: schema,
});

const { value: email } = useField<string>("email", undefined, {
  initialValue: props.email,
});

const { value: firstName } = useField<string>("firstName", undefined);
const lastName = ref("");

const { value: password } = useField<string>("password");

const hasMinLength = computed(() => password.value?.length >= 8);
const hasLetter = computed(() => /[a-zA-Z]/.test(password.value || ""));
const hasNumber = computed(() => /\d/.test(password.value || ""));
const hasSpecial = computed(() => /[@$!%*?&]/.test(password.value || ""));

const hasValidated = ref(false);
const isLoading = ref(false);

const createNewUser = async () => {
  if (isLoading.value) {
    return;
  }

  isLoading.value = true;
  hasValidated.value = true;

  const { valid } = await validate();
  if (!valid) {
    return;
  }

  try {
    const userCredential = await createUserWithEmailAndPassword(
      auth,
      email.value,
      password.value
    );

    const user = userCredential.user;

    await updateProfile(user, {
      displayName: `${firstName.value.trim()} ${lastName.value.trim()}`,
    });

    await sendEmailVerification(user, {
      url: `https://${window.location.host}/u`,
    });
    router.push({ name: "EmailVerification" });
  } catch (e: any) {
    console.error(e);
  }
};
</script>

<template>
  <div class="login_container">
    <a class="link top" @click="emits('back')">
      <span class="material-symbols-outlined"> arrow_back </span>
      <span class="text">
        {{ $t("login.back") }}
      </span>
    </a>

    <h2 class="login_header">
      {{ $t("login.signUp.header") }}
    </h2>

    <form @submit.prevent="createNewUser" novalidate>
      <div>
        <label>{{ $t("login.email.label") }}</label>
        <div class="input_container">
          <input
            class="input"
            v-model="email"
            type="email"
            placeholder="Email"
            required
          />
          <p
            class="help"
            :class="{ visible: errors.email !== undefined && hasValidated }"
          >
            {{ errors.email }}
          </p>
        </div>
      </div>

      <div class="cols">
        <div class="col">
          <label>{{ $t("login.firstName.label") }}</label>
          <div class="input_container">
            <input class="input" v-model="firstName" />
            <p
              class="help"
              :class="{
                visible: errors.firstName !== undefined && hasValidated,
              }"
            >
              {{ errors.firstName }}
            </p>
          </div>
        </div>
        <div class="col">
          <label>{{ $t("login.lastName.label") }}</label>
          <div class="input_container">
            <input class="input" v-model="lastName" />
          </div>
        </div>
      </div>

      <div>
        <label>{{ $t("login.password.label") }}</label>
        <div class="input_container">
          <div class="input_field_container">
            <input
              class="input"
              v-model="password"
              :type="showPassword ? 'text' : 'password'"
              placeholder="Password"
              autocomplete="new-password"
              required
            />
            <span
              class="material-symbols-outlined icon_button"
              @click="showPassword = !showPassword"
            >
              {{ showPassword ? "visibility_off" : "visibility" }}
            </span>
          </div>
          <p
            class="help"
            :class="{ visible: errors.password !== undefined && hasValidated }"
          >
            {{ errors.password }}
          </p>
        </div>

        <ul class="password_rules">
          <li :class="{ passed: hasMinLength }">
            <span class="material-symbols-outlined">
              {{ hasMinLength ? "check" : "close" }}
            </span>
            {{ t("login.password.requirements.minLength") }}
          </li>
          <li :class="{ passed: hasLetter }">
            <span class="material-symbols-outlined">
              {{ hasLetter ? "check" : "close" }}
            </span>
            {{ t("login.password.requirements.hasLetter") }}
          </li>
          <li :class="{ passed: hasNumber }">
            <span class="material-symbols-outlined">
              {{ hasNumber ? "check" : "close" }}
            </span>
            {{ t("login.password.requirements.hasNumber") }}
          </li>
          <li :class="{ passed: hasSpecial }">
            <span class="material-symbols-outlined">
              {{ hasSpecial ? "check" : "close" }}
            </span>
            {{ t("login.password.requirements.hasSpecial") }}
          </li>
        </ul>
      </div>

      <button v-if="!isLoading" class="primary button" type="submit">
        {{ t("login.signUp.submit.label") }}
      </button>
      <button v-else class="primary button disabled">
        {{ t("login.signUp.submit.loading") }}
      </button>
    </form>
  </div>
</template>

<style src="./login.scss" scoped lang="scss"></style>

<style lang="scss" scoped>
.cols {
  display: flex;
}

.cols .col {
  margin-right: 0.5em;
}

.cols .col:last-child {
  margin-right: 0;
}

ul.password_rules {
  padding: 0;
  margin: 0 0 1em 0;
}

ul.password_rules li {
  list-style: none;
  font-size: 0.7em;
  display: flex;
  align-items: center;
  line-height: 1.8;
}

ul.password_rules li.passed {
  color: var(--classic-success, #48c78e);
}

ul.password_rules li .material-symbols-outlined {
  font-size: 0.8rem;
  background: var(--classic-surface-inset, #bbb5ae);
  border-radius: 50%;
  color: var(--classic-text, #fff);
  padding: 1px;
  margin-right: 0.5em;
}

ul.password_rules li.passed .material-symbols-outlined {
  background-color: var(--classic-success, #48c78e);
  color: var(--classic-on-accent, #fff);
}

.input_container {
  padding-bottom: 0.6em;
}
</style>
